import { useCallback, useEffect, useState } from "react";
import { authFetchJson } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

type LogSource = "audit" | "usage";

type AuditLogEntry = {
  id: string;
  actor_id: string;
  actor_email?: string;
  actor_role: string;
  action: string;
  target_user_id: string | null;
  target_email?: string;
  target_resource: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
};

type UsageLogEntry = {
  id: string;
  user_id: string;
  user_email?: string;
  mode: string | null;
  source: string | null;
  processing_time_ms: number | null;
  success: boolean;
  error_type: string | null;
  model: string | null;
  input_tokens: number;
  output_tokens: number;
  audio_seconds: number;
  estimated_cost: number;
  created_at: string;
};

type LogsResponse = {
  audit_logs: AuditLogEntry[];
  usage_logs: UsageLogEntry[];
  total_audit: number;
  total_usage: number;
};

function truncId(id: string | null | undefined): string {
  if (!id) return "—";
  return id.slice(0, 8) + "…";
}

const ACTION_COLORS: Record<string, string> = {
  delete_user: "bg-red-100 text-red-700",
  feedback_audio_access: "bg-amber-100 text-amber-700",
  settings_import: "bg-blue-100 text-blue-700",
  impersonate: "bg-purple-100 text-purple-700",
};

function ActionBadge({ action }: { action: string }) {
  const color = ACTION_COLORS[action] ?? "bg-[var(--cs-ivory)] text-[rgba(10,52,96,0.78)]";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${color}`}>
      {action}
    </span>
  );
}

function SuccessBadge({ success }: { success: boolean }) {
  return success ? (
    <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
      ok
    </span>
  ) : (
    <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
      fout
    </span>
  );
}

export function AdminLogsTab() {
  const [source, setSource] = useState<LogSource>("audit");
  const [data, setData] = useState<LogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterText, setFilterText] = useState("");
  const [days, setDays] = useState(7);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await authFetchJson<LogsResponse>(
        `/api/admin/logs?days=${days}&limit=500`,
      );
      setData(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kon logs niet ophalen.");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredAudit = filterText
    ? (data?.audit_logs ?? []).filter(
        (l) =>
          (l.action + (l.actor_email ?? l.actor_id) + (l.target_email ?? "") + (l.target_resource ?? ""))
            .toLowerCase()
            .includes(filterText.toLowerCase()),
      )
    : data?.audit_logs ?? [];

  const filteredUsage = filterText
    ? (data?.usage_logs ?? []).filter(
        (l) =>
          (
            (l.user_email ?? l.user_id) +
            (l.mode ?? "") +
            (l.source ?? "") +
            (l.model ?? "") +
            (l.error_type ?? "")
          )
            .toLowerCase()
            .includes(filterText.toLowerCase()),
      )
    : data?.usage_logs ?? [];

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-[rgba(10,52,96,0.10)] bg-white">
          {(["audit", "usage"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSource(s)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors first:rounded-l-lg last:rounded-r-lg ${
                source === s
                  ? "bg-rose-50 text-rose-700"
                  : "text-[var(--cs-ink-muted)] hover:text-[var(--cs-navy)]"
              }`}
            >
              {s === "audit" ? "Audit log" : "Gebruik log"}
              <span className="ml-1.5 rounded-full bg-[var(--cs-ivory)] px-1.5 py-0.5 text-[10px] text-[var(--cs-ink-muted)]">
                {s === "audit" ? filteredAudit.length : filteredUsage.length}
              </span>
            </button>
          ))}
        </div>

        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-lg border border-[rgba(10,52,96,0.18)] px-2 py-1.5 text-xs"
        >
          <option value={1}>Laatste 24u</option>
          <option value={7}>Laatste 7 dagen</option>
          <option value={30}>Laatste 30 dagen</option>
          <option value={90}>Laatste 90 dagen</option>
        </select>

        <input
          type="text"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder="Filter op actie, email, bron, model…"
          className="flex-1 rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
        />

        <button
          type="button"
          onClick={() => void load()}
          className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1.5 text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)]"
        >
          Vernieuwen
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p> : null}

      {/* Audit Logs */}
      {source === "audit" && !loading ? (
        filteredAudit.length === 0 ? (
          <p className="text-sm text-[var(--cs-ink-muted)]">Geen audit logs gevonden.</p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
            <table className="w-full text-sm">
              <thead className="bg-[var(--cs-ivory)]">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                  <th className="px-4 py-2">Tijdstip</th>
                  <th className="px-4 py-2">Actie</th>
                  <th className="px-4 py-2">Actor</th>
                  <th className="px-4 py-2">Doel</th>
                  <th className="px-4 py-2">Resource</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
                {filteredAudit.map((log) => (
                  <AuditRow
                    key={log.id}
                    log={log}
                    expanded={expandedId === log.id}
                    onToggle={() =>
                      setExpandedId(expandedId === log.id ? null : log.id)
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {/* Usage Logs */}
      {source === "usage" && !loading ? (
        filteredUsage.length === 0 ? (
          <p className="text-sm text-[var(--cs-ink-muted)]">Geen gebruik logs gevonden.</p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
            <table className="w-full text-sm">
              <thead className="bg-[var(--cs-ivory)]">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                  <th className="px-4 py-2">Tijdstip</th>
                  <th className="px-4 py-2">Gebruiker</th>
                  <th className="px-4 py-2">Mode</th>
                  <th className="px-4 py-2">Bron</th>
                  <th className="px-4 py-2">Model</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Duur</th>
                  <th className="px-4 py-2">Kosten</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
                {filteredUsage.map((log) => (
                  <UsageRow
                    key={log.id}
                    log={log}
                    expanded={expandedId === log.id}
                    onToggle={() =>
                      setExpandedId(expandedId === log.id ? null : log.id)
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
}

function AuditRow({
  log,
  expanded,
  onToggle,
}: {
  log: AuditLogEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className="cursor-pointer text-xs hover:bg-[var(--cs-ivory)]"
        onClick={onToggle}
      >
        <td className="whitespace-nowrap px-4 py-2 tabular-nums text-[rgba(10,52,96,0.78)]">
          {formatDateTime(log.created_at, { seconds: true })}
        </td>
        <td className="px-4 py-2">
          <ActionBadge action={log.action} />
        </td>
        <td className="px-4 py-2">
          <span className="text-[var(--cs-navy)]">{log.actor_email ?? truncId(log.actor_id)}</span>
          <span className="ml-1 text-[10px] text-[var(--cs-ink-subtle)]">{log.actor_role}</span>
        </td>
        <td className="px-4 py-2 text-[rgba(10,52,96,0.78)]">
          {log.target_email ?? truncId(log.target_user_id)}
        </td>
        <td className="px-4 py-2 font-mono text-[10px] text-[var(--cs-ink-muted)]">
          {log.target_resource ?? "—"}
        </td>
      </tr>
      {expanded && Object.keys(log.metadata).length > 0 ? (
        <tr>
          <td colSpan={5} className="bg-[var(--cs-ivory)] px-4 py-3">
            <pre className="max-h-40 overflow-auto rounded bg-[var(--cs-ivory)] p-2 text-[10px] text-[rgba(10,52,96,0.78)]">
              {JSON.stringify(log.metadata, null, 2)}
            </pre>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function UsageRow({
  log,
  expanded,
  onToggle,
}: {
  log: UsageLogEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className="cursor-pointer text-xs hover:bg-[var(--cs-ivory)]"
        onClick={onToggle}
      >
        <td className="whitespace-nowrap px-4 py-2 tabular-nums text-[rgba(10,52,96,0.78)]">
          {formatDateTime(log.created_at, { seconds: true })}
        </td>
        <td className="px-4 py-2 text-[var(--cs-navy)]">
          {log.user_email ?? truncId(log.user_id)}
        </td>
        <td className="px-4 py-2">
          <span className="rounded bg-[var(--cs-ivory)] px-1.5 py-0.5 text-[10px] font-medium text-[rgba(10,52,96,0.78)]">
            {log.mode ?? "—"}
          </span>
        </td>
        <td className="px-4 py-2 font-mono text-[10px] text-[var(--cs-ink-muted)]">
          {log.source ?? "—"}
        </td>
        <td className="px-4 py-2 font-mono text-[10px] text-[var(--cs-ink-muted)]">
          {log.model ?? "—"}
        </td>
        <td className="px-4 py-2">
          <SuccessBadge success={log.success} />
          {log.error_type ? (
            <span className="ml-1 text-[10px] text-red-500">{log.error_type}</span>
          ) : null}
        </td>
        <td className="whitespace-nowrap px-4 py-2 tabular-nums text-[rgba(10,52,96,0.78)]">
          {log.processing_time_ms != null
            ? log.processing_time_ms > 1000
              ? `${(log.processing_time_ms / 1000).toFixed(1)}s`
              : `${log.processing_time_ms}ms`
            : "—"}
        </td>
        <td className="whitespace-nowrap px-4 py-2 tabular-nums text-[rgba(10,52,96,0.78)]">
          ${log.estimated_cost?.toFixed(4) ?? "0.0000"}
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={8} className="bg-[var(--cs-ivory)] px-4 py-3">
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs sm:grid-cols-4">
              <Detail label="ID" value={log.id} />
              <Detail label="User ID" value={log.user_id} />
              <Detail label="Source" value={log.source ?? "—"} />
              <Detail label="Input tokens" value={String(log.input_tokens ?? 0)} />
              <Detail label="Output tokens" value={String(log.output_tokens ?? 0)} />
              <Detail label="Audio (sec)" value={String(log.audio_seconds?.toFixed(1) ?? "0")} />
              <Detail label="Processing" value={`${log.processing_time_ms ?? 0}ms`} />
              <Detail label="Kosten" value={`$${log.estimated_cost?.toFixed(4) ?? "0.0000"}`} />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-[var(--cs-ink-muted)]">{label}: </span>
      <span className="font-medium text-[var(--cs-navy)]">{value}</span>
    </div>
  );
}
